import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCalculoConcepto } from '../../../../../api'
import { InvoiceConceptModel } from '../../../../../api/Argentia/endpoints/invoices/model/invoices'
import { ProductConceptModel } from '../../../../../api/Argentia/model/contracts'
import { CardActionProps } from '../../../../../components/shared/cardActions/cardActions'
import SimpleDialog from '../../../../../components/shared/simpleDialog/simpleDialog'
import { getCustomerDomain } from '../../../../../redux'
import { PERCENTAGE_CALC_TYPES } from '../invoiceConceptsTable/invoiceConceptsTable'

// ─── Types ────────────────────────────────────────────────────────────────────

export type InvoiceConceptFormMode = 'add' | 'edit'

export type InvoiceConceptFormData = Partial<InvoiceConceptModel>

// ─── Props ────────────────────────────────────────────────────────────────────

interface InvoiceConceptFormDialogProps {
  open: boolean
  mode: InvoiceConceptFormMode
  isPercentage: boolean
  initialData?: InvoiceConceptFormData
  conceptsList?: ProductConceptModel[]
  defaultStartDate?: Date
  defaultEndDate?: Date
  onClose: (formData?: InvoiceConceptFormData) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildEmptyForm = (isPercentage: boolean): InvoiceConceptFormData => ({
  conceptCode: '',
  conceptName: '',
  conceptDetail: '',
  calculationType: isPercentage
    ? EnumTipoCalculoConcepto.Porcentaje
    : EnumTipoCalculoConcepto.PrecioUnitario,
  startDate: undefined,
  endDate: undefined,
  quantity: undefined,
  price: undefined,
  base: undefined,
  percentage: undefined
})

// ─── Component ────────────────────────────────────────────────────────────────

export const InvoiceConceptFormDialog: React.FC<InvoiceConceptFormDialogProps> = ({
  open,
  mode,
  isPercentage,
  initialData,
  conceptsList = [],
  defaultStartDate,
  defaultEndDate,
  onClose
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceEditPage'])

  const [formData, setFormData] = useState<InvoiceConceptFormData>(buildEmptyForm(isPercentage))

  // ─── Calc type select options (filtered by table type) ──────────────────────

  const calcTypeSelectOptions = useMemo(() => {
    const allEntries = Object.entries(EnumTipoCalculoConcepto).filter(([key]) => isNaN(Number(key)))
    const filtered = isPercentage
      ? Array.from(PERCENTAGE_CALC_TYPES)
          .map(val => allEntries.find(([, v]) => Number(v) === val))
          .filter((e): e is [string, string] => e !== undefined)
      : allEntries.filter(([, val]) => !PERCENTAGE_CALC_TYPES.has(Number(val)))
    return filtered.map(([key, val]) => ({ label: key, value: Number(val) }))
  }, [isPercentage])

  const isEditMode = mode === 'edit'

  const conceptSelectOptions = useMemo(
    (): SelectOption[] =>
      conceptsList
        .filter(c =>
          isPercentage
            ? PERCENTAGE_CALC_TYPES.has(c.calculationType)
            : !PERCENTAGE_CALC_TYPES.has(c.calculationType)
        )
        .map(c => ({ value: c.conceptId, label: `${c.code} - ${c.description}` })),
    [conceptsList, isPercentage]
  )

  const handleConceptSelect = (conceptId: number) => {
    const found = conceptsList.find(c => c.conceptId === Number(conceptId))
    if (!found) return
    setFormData(prev => ({
      ...prev,
      conceptCode: found.code,
      conceptName: found.description,
      conceptDetail: found.description,
      calculationType: found.calculationType
    }))
  }

  // ─── Validation ─────────────────────────────────────────────────────────────

  const dateRangeValid = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return true
    return new Date(formData.endDate) > new Date(formData.startDate)
  }, [formData.startDate, formData.endDate])

  // ─── Sections ───────────────────────────────────────────────────────────────

  const sections: Section[] = useMemo(
    () => [
      {
        inputs: [
          // ── Concept selector (add mode only) ──────────────────────────────
          {
            name: 'conceptSelector',
            label: t('invoiceEditPage:concepts.columns.concept'),
            type: DataType.SELECT,
            selectOptions: conceptSelectOptions,
            required: true,
            inputSize: 12,
            display: !isEditMode,
            onChange: (value, _formState) => {
              if (value != null) handleConceptSelect(Number(value))
            }
          },
          {
            name: 'conceptCode',
            label: t('invoiceEditPage:concepts.columns.code'),
            type: DataType.TEXT,
            required: true,
            inputSize: 6,
            display: isEditMode,
            readOnly: true,
            onChange: (_value, formState) => setFormData(formState as InvoiceConceptFormData)
          },
          {
            name: 'conceptName',
            label: t('invoiceEditPage:concepts.columns.concept'),
            type: DataType.TEXT,
            inputSize: 6,
            display: isEditMode,
            readOnly: true,
            onChange: (_value, formState) => setFormData(formState as InvoiceConceptFormData)
          },
          {
            name: 'conceptDetail',
            label: t('invoiceEditPage:concepts.columns.conceptDetail'),
            type: DataType.TEXT,
            inputSize: 12,
            display: isEditMode,
            readOnly: true,
            onChange: (_value, formState) => setFormData(formState as InvoiceConceptFormData)
          },
          {
            name: 'calculationType',
            label: t('invoiceEditPage:concepts.columns.calculationType'),
            type: DataType.SELECT,
            selectOptions: calcTypeSelectOptions,
            required: true,
            inputSize: 12,
            readOnly: true,
            onChange: (value, formState) => {
              setFormData({ ...(formState as InvoiceConceptFormData), calculationType: +value })
            }
          },
          {
            name: 'startDate',
            label: t('invoiceEditPage:concepts.columns.startDate'),
            type: DataType.DATE,
            required: true,
            inputSize: 6,
            onChange: (value, formState) => {
              setFormData({
                ...(formState as InvoiceConceptFormData),
                startDate: value as unknown as Date
              })
            }
          },
          {
            name: 'endDate',
            label: t('invoiceEditPage:concepts.columns.endDate'),
            type: DataType.DATE,
            required: true,
            inputSize: 6,
            errorState: !dateRangeValid,
            errorText: !dateRangeValid
              ? t(
                  'invoiceEditPage:concepts.validation.endDateAfterStart',
                  'La fecha de fin debe ser posterior a la fecha de inicio'
                )
              : undefined,
            onChange: (value, formState) => {
              setFormData({
                ...(formState as InvoiceConceptFormData),
                endDate: value as unknown as Date
              })
            }
          },
          // ── Percentage fields ──────────────────────────────────────────────
          {
            name: 'base',
            label: t('invoiceEditPage:concepts.columns.base'),
            type: DataType.NUMBER,
            customDecimalScale: 12,
            required: true,
            inputSize: 6,
            display: isPercentage,
            readOnly: isEditMode, // base is read-only in edit mode; only percentage is editable
            onChange: (value, formState) => {
              setFormData({ ...(formState as InvoiceConceptFormData), base: value as number })
            }
          },
          {
            name: 'percentage',
            label: t('invoiceEditPage:concepts.columns.percentage'),
            type: DataType.NUMBER,
            customDecimalScale: 12,
            required: true,
            inputSize: 6,
            display: isPercentage,
            onChange: (value, formState) => {
              setFormData({ ...(formState as InvoiceConceptFormData), percentage: value as number })
            }
          },
          // ── Regular fields ─────────────────────────────────────────────────
          {
            name: 'quantity',
            label: t('invoiceEditPage:concepts.columns.quantity'),
            type: DataType.NUMBER,
            required: true,
            inputSize: 6,
            display: !isPercentage,
            onChange: (value, formState) => {
              setFormData({ ...(formState as InvoiceConceptFormData), quantity: value as number })
            }
          },
          {
            name: 'price',
            label: t('invoiceEditPage:concepts.columns.price'),
            type: DataType.NUMBER,
            customDecimalScale: 12,
            required: true,
            inputSize: 6,
            display: !isPercentage,
            onChange: (value, formState) => {
              setFormData({ ...(formState as InvoiceConceptFormData), price: value as number })
            }
          }
        ]
      }
    ],
    [t, isPercentage, calcTypeSelectOptions, conceptSelectOptions, isEditMode, dateRangeValid]
  )

  const formOptions: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 2,
      analytics: {
        customer: getCustomerDomain(),
        view: 'invoiceEditPage:conceptFormDialog'
      }
    }),
    []
  )

  // ─── Validation (continued) ─────────────────────────────────────────────────

  const formValid = useMemo(() => {
    const datesValid = !!formData.startDate && !!formData.endDate && dateRangeValid
    if (isEditMode) return datesValid

    const baseValid = isPercentage
      ? formData.base != null && formData.percentage != null
      : formData.quantity != null && formData.price != null

    return datesValid && !!formData.conceptCode && !!formData.calculationType && baseValid
  }, [formData, isEditMode, isPercentage, dateRangeValid])

  // ─── Footer actions ─────────────────────────────────────────────────────────

  const footerActions: CardActionProps[] = useMemo(
    () => [
      {
        label: t('invoiceEditPage:concepts.dialog.cancel'),
        color: 'secondary',
        handleActionClick: () => onClose(undefined)
      },
      {
        label: t('invoiceEditPage:concepts.dialog.confirm'),
        color: 'primary',
        disabled: !formValid,
        handleActionClick: () => onClose(formData)
      }
    ],
    [t, formValid, formData, onClose]
  )

  // ─── Reset on open ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      setFormData(
        mode === 'edit' && initialData
          ? { ...buildEmptyForm(isPercentage), ...initialData }
          : {
              ...buildEmptyForm(isPercentage),
              startDate: defaultStartDate,
              endDate: defaultEndDate
            }
      )
    }
  }, [open, mode, initialData, isPercentage, defaultStartDate, defaultEndDate])

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <SimpleDialog
      open={open}
      handleClose={() => onClose(undefined)}
      footerActions={footerActions}
      maxWidth="sm"
      formStyle="formDialog"
    >
      <Form
        title={
          mode === 'edit'
            ? t('invoiceEditPage:concepts.dialog.titleEdit')
            : t('invoiceEditPage:concepts.dialog.titleAdd')
        }
        sections={sections}
        data={formData}
        options={formOptions}
      />
    </SimpleDialog>
  )
}
